import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
    extractApiErrorMessage,
    handlePlanningCreationSuccess,
} from "../../../../_helpers/planningHourLimitNotifications";

import { Button } from "../../../../_helpers/components/Button";
import { InputForm } from "../../../../_helpers/components/Forms/InputLabel";
import { DefaultDropdown } from "../../../../_helpers/components/Forms/Dropdown";
import {
    createPlanning,
    createPlanningReal,
    cancelPlanningReal,
    recalculatePlanningCounters,
} from "../../../../_api/WorkShift";

const TYPE_COPY_OPTIONS = [
    // { label: "Ne rien faire", value: 0 },
    { label: "Créer le planning sur les journées vides", value: 1 },
    { label: "Ne pas modifier les absences", value: 2 },
    { label: "Tout remplacer", value: 3 },
];

const toDateInput = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const yesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return toDateInput(d);
};

const formatDisplayDate = (value) => {
    if (!value) return "Aucune intégration";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("fr-FR");
};

const OperationCard = ({ title, description, children, onSubmit, loading, submitLabel }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
        {description && <p className="text-sm text-gray-500 mb-4">{description}</p>}
        <div className="space-y-4">{children}</div>
        <div className="mt-4">
            <Button
                name={submitLabel}
                action={onSubmit}
                loading={loading}
            />
        </div>
    </div>
);

const OperationsTab = ({ profile, refreshProfil }) => {
    const [loadingProvisional, setLoadingProvisional] = useState(false);
    const [loadingReal, setLoadingReal] = useState(false);
    const [loadingCancel, setLoadingCancel] = useState(false);
    const [loadingRecalc, setLoadingRecalc] = useState(false);

    const [typeCopy, setTypeCopy] = useState(1);
    const [provisionalStart, setProvisionalStart] = useState("");
    const [provisionalEnd, setProvisionalEnd] = useState("");

    const [integrationDate, setIntegrationDate] = useState(yesterday());
    const [cancelDate, setCancelDate] = useState("");
    const [integrationDebug, setIntegrationDebug] = useState(null);

    const [recalcStart, setRecalcStart] = useState("");
    const [recalcEnd, setRecalcEnd] = useState("");
    const [recalcDebug, setRecalcDebug] = useState(null);

    const maxIntegrationDate = yesterday();
    const maxCancelDate = useMemo(() => {
        if (!profile?.last_date_planning_real) return "";
        return toDateInput(new Date(profile.last_date_planning_real));
    }, [profile?.last_date_planning_real]);

    const handleCreateProvisional = async () => {
        if (typeCopy === 0) return;
        if (!provisionalStart || !provisionalEnd) {
            toast.error("Veuillez renseigner les dates de début et de fin.");
            return;
        }
        setLoadingProvisional(true);
        try {
            const payload = {
                user_id: profile.id,
                start_date: provisionalStart,
                end_date: provisionalEnd,
                type_copy:
                    typeCopy === 1
                        ? "if_nothing"
                        : typeCopy === 2
                          ? "if_not_absence"
                          : "full",
                destinations: ["PROVISIONAL"],
            };
            const resp = await createPlanning(payload);
            handlePlanningCreationSuccess(resp.data);
            toast.success(resp.data.message || "Planning prévisionnel créé.");
        } catch (error) {
            const msg = extractApiErrorMessage(
                error?.response?.data,
                "Erreur lors de la création du planning prévisionnel."
            );
            toast.error(msg);
        } finally {
            setLoadingProvisional(false);
        }
    };

    const handleIntegrateReal = async () => {
        if (!integrationDate) {
            toast.error("Veuillez renseigner une date.");
            return;
        }
        if (integrationDate > maxIntegrationDate) {
            toast.error("La date ne peut pas être postérieure à hier.");
            return;
        }
        setLoadingReal(true);
        setIntegrationDebug(null);
        try {
            const resp = await createPlanningReal({
                user_id: profile.id,
                date: integrationDate,
            });
            toast.success(resp.data.message || "Planning réalisé généré.");
            setIntegrationDebug(resp.data?.data?.debug ?? null);
            refreshProfil?.();
        } catch {
            toast.error("Erreur lors de l'intégration du planning réalisé.");
        } finally {
            setLoadingReal(false);
        }
    };

    const handleCancelIntegration = async () => {
        if (!cancelDate) {
            toast.error("Veuillez renseigner une date.");
            return;
        }
        if (!profile?.last_date_planning_real) {
            toast.error("Aucune intégration à annuler.");
            return;
        }
        if (maxCancelDate && cancelDate > maxCancelDate) {
            toast.error("La date ne peut pas être postérieure à la dernière intégration.");
            return;
        }
        setLoadingCancel(true);
        try {
            const resp = await cancelPlanningReal({
                user_id: profile.id,
                date: cancelDate,
            });
            toast.success(resp.data.message || "Intégration annulée.");
            refreshProfil?.();
        } catch {
            toast.error("Erreur lors de l'annulation de l'intégration.");
        } finally {
            setLoadingCancel(false);
        }
    };

    const handleRecalculateCounters = async () => {
        if (!recalcStart || !recalcEnd) {
            toast.error("Veuillez renseigner les dates de début et de fin.");
            return;
        }
        if (recalcStart > recalcEnd) {
            toast.error("La date de début ne peut pas être postérieure à la date de fin.");
            return;
        }
        setLoadingRecalc(true);
        setRecalcDebug(null);
        try {
            const resp = await recalculatePlanningCounters({
                user_id: profile.id,
                start_date: recalcStart,
                end_date: recalcEnd,
            });
            toast.success(resp.data.message || "Compteurs recalculés.");
            setRecalcDebug(resp.data?.data?.debug ?? null);
        } catch {
            toast.error("Erreur lors du recalcul des compteurs.");
        } finally {
            setLoadingRecalc(false);
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
                <p className="text-sm text-gray-600">
                    Le planning réalisé a été créé jusqu'au :{" "}
                    <span className="font-medium text-gray-900">
                        {formatDisplayDate(profile?.last_date_planning_real)}
                    </span>
                </p>
            </div>

            <OperationCard
                title="Créer le planning prévisionnel"
                description="Génère le planning prévisionnel sur la période choisie."
                onSubmit={handleCreateProvisional}
                loading={loadingProvisional}
                submitLabel="Créer le prévisionnel"
            >
                <DefaultDropdown
                    name="Mode de création"
                    options={TYPE_COPY_OPTIONS}
                    value={typeCopy}
                    onChange={(e) => setTypeCopy(Number(e.target.value))}
                />
                {typeCopy > 0 && (
                    <>
                        <InputForm
                            type="date"
                            name="Date de début"
                            value={provisionalStart}
                            set={setProvisionalStart}
                        />
                        <InputForm
                            type="date"
                            name="Date de fin"
                            value={provisionalEnd}
                            set={setProvisionalEnd}
                        />
                    </>
                )}
            </OperationCard>

            <OperationCard
                title="Recalculer les compteurs"
                description="Force le recalcul des mouvements sur la période choisie."
                onSubmit={handleRecalculateCounters}
                loading={loadingRecalc}
                submitLabel="Recalculer"
            >
                <InputForm
                    type="date"
                    name="Date de début"
                    value={recalcStart}
                    set={setRecalcStart}
                />
                <InputForm
                    type="date"
                    name="Date de fin"
                    value={recalcEnd}
                    set={setRecalcEnd}
                />
                {recalcDebug && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-800 mb-2">
                            Debug temporaire — recalcul compteurs
                        </p>
                        <pre className="text-xs text-amber-950 overflow-auto max-h-96 whitespace-pre-wrap wrap-break-word">
                            {JSON.stringify(recalcDebug, null, 2)}
                        </pre>
                    </div>
                )}
            </OperationCard>

            <OperationCard
                title="Intégrer le planning réalisé"
                description="Crée le planning réalisé jusqu'à la date choisie (maximum hier)."
                onSubmit={handleIntegrateReal}
                loading={loadingReal}
                submitLabel="Intégrer"
            >
                <InputForm
                    type="date"
                    name="Intégrer jusqu'au"
                    value={integrationDate}
                    set={setIntegrationDate}
                    max={maxIntegrationDate}
                />
                {integrationDebug && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-800 mb-2">
                            Debug temporaire — intégration REAL
                        </p>
                        <pre className="text-xs text-amber-950 overflow-auto max-h-96 whitespace-pre-wrap wrap-break-word">
                            {JSON.stringify(integrationDebug, null, 2)}
                        </pre>
                    </div>
                )}
            </OperationCard>

            <OperationCard
                title="Annuler une intégration"
                description="Supprime le planning réalisé jusqu'à la date choisie et recalcule les compteurs concernés."
                onSubmit={handleCancelIntegration}
                loading={loadingCancel}
                submitLabel="Annuler l'intégration"
            >
                <InputForm
                    type="date"
                    name="Annuler jusqu'au"
                    value={cancelDate}
                    set={setCancelDate}
                    max={maxCancelDate || undefined}
                />
            </OperationCard>
        </div>
    );
};

export default OperationsTab;
